import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@mlm/ui';
import { getTenantConfig, listMyOrders } from '../lib/api';

const SESSION_FLAG = 'omni.unpaidDialogShown';
const UNPAID_STATUSES = ['pending', 'to_pay'];

/**
 * Greets a member on login when they have unpaid order(s) and offers to jump to
 * the payment flow. The message is admin-editable (tenant
 * `unpaid_orders_dialog_message`). Shows once per session — the flag is cleared
 * on logout (the session ends, so sessionStorage is naturally wiped on a new
 * login from a fresh tab; we also clear it explicitly when dismissed so a
 * re-login in the same tab re-arms it only after this one is acted on).
 */
export function UnpaidOrdersGate() {
  const navigate = useNavigate();
  const alreadyShown =
    typeof window !== 'undefined' && window.sessionStorage.getItem(SESSION_FLAG) === '1';
  const [dismissed, setDismissed] = useState(alreadyShown);

  const ordersQ = useQuery({
    queryKey: ['unpaid-orders'],
    queryFn: () => listMyOrders(undefined, { statuses: UNPAID_STATUSES, limit: 50 }),
    enabled: !dismissed,
    staleTime: 60_000,
  });
  const tenantQ = useQuery({ queryKey: ['tenant-config'], queryFn: getTenantConfig });

  const unpaid = ordersQ.data ?? [];
  if (dismissed || unpaid.length === 0) return null;

  const message =
    tenantQ.data?.unpaidOrdersDialogMessage?.trim() ||
    'You have unpaid order/s, would you like to proceed with the payment steps?';

  function close() {
    if (typeof window !== 'undefined') window.sessionStorage.setItem(SESSION_FLAG, '1');
    setDismissed(true);
  }

  function proceed() {
    const first = unpaid[0];
    close();
    navigate(first ? `/orders/${first.id}` : '/orders');
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Unpaid orders"
    >
      <div className="w-full max-w-md rounded-lg border bg-card p-6 shadow-elevated">
        <h2 className="text-h2">Unpaid order{unpaid.length > 1 ? 's' : ''}</h2>
        <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">{message}</p>
        <div className="mt-5 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={close}
            className="text-sm font-medium text-muted-foreground hover:text-foreground"
          >
            Later
          </button>
          <Button onClick={proceed}>Proceed to payment</Button>
        </div>
      </div>
    </div>
  );
}
